import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton
} from '@mui/material';
import {
  Search as SearchIcon,
  Visibility as ViewIcon,
  TrendingUp as TrendingUpIcon,
  People as PeopleIcon,
  Business as BusinessIcon
} from '@mui/icons-material';
import { useOrg } from '../../contexts/OrgContext';
import Breadcrumbs from '../../components/Breadcrumbs';
import KpiCard from '../../components/crm/KpiCard';
import StatusBadge from '../../components/crm/StatusBadge';
import { getCompanies } from '../../services/mock/companyService';

const CompanyManagement = () => {
  const { org } = useOrg();
  const navigate = useNavigate();
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [tierFilter, setTierFilter] = useState('all');

  const showGMV = process.env.REACT_APP_SHOW_GMV === 'true';

  useEffect(() => {
    loadCompanies();
  }, [org]);

  const loadCompanies = async () => {
    setLoading(true);
    try {
      const companiesData = await getCompanies(org);
      setCompanies(companiesData);
    } catch (error) {
      console.error('Error loading companies:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleViewCompany = (companyId) => {
    navigate(`/internal/${org}/companies/${companyId}`);
  };

  const filteredCompanies = companies.filter(company => {
    const matchesSearch = company.name.toLowerCase().includes(search.toLowerCase()) ||
                         `${company.city}, ${company.state}`.toLowerCase().includes(search.toLowerCase());
    const matchesStatus = statusFilter === 'all' || company.status === statusFilter;
    const matchesTier = tierFilter === 'all' || company.tier === tierFilter;
    return matchesSearch && matchesStatus && matchesTier;
  });

  const formatCurrency = (value) => `$${value?.toLocaleString() || 0}`;

  // Calculate KPIs
  const totalCompanies = companies.length;
  const activeCompanies = companies.filter(c => c.status === 'Active').length;
  const totalRevenue = companies.reduce((sum, c) => sum + (c.revenue12m || 0), 0);
  const totalClinics = companies.reduce((sum, c) => sum + c.clinics, 0);

  if (loading) {
    return <Box sx={{ p: 3 }}>Loading...</Box>;
  }

  return (
    <Box>
      <Breadcrumbs />
      
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box>
            <Typography variant="h3" component="h1" sx={{ fontWeight: 700, color: '#0f172a', mb: 1 }}>
              Company Management
            </Typography>
            <Typography variant="h6" sx={{ color: '#64748b', fontWeight: 500 }}>
              {org === 'bare' ? 'Bare Biologics - Client Portfolio' : 'EPL - Client Portfolio'}
            </Typography>
          </Box>
          
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="outlined"
              onClick={() => navigate(`/internal/${org}/client-management`)}
            >
              Back to Dashboard
            </Button>
          </Box>
        </Box>
      </Box>

      {/* KPI Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard
            title="Total Companies"
            value={totalCompanies}
            icon={<BusinessIcon />}
            color={org === 'bare' ? '#5c725c' : '#0d9488'}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard
            title="Active Companies"
            value={activeCompanies}
            subtitle={`${Math.round((activeCompanies/totalCompanies)*100)}% of total`}
            icon={<PeopleIcon />}
            color="#10b981"
          />
        </Grid>
        {showGMV && (
          <Grid item xs={12} sm={6} md={3}>
            <KpiCard
              title="Total Revenue"
              value={totalRevenue}
              subtitle="last 12 months"
              icon={<TrendingUpIcon />}
              color="#f59e0b"
              formatter={formatCurrency}
            />
          </Grid>
        )}
        <Grid item xs={12} sm={6} md={showGMV ? 3 : 4}>
          <KpiCard
            title="Total Clinics"
            value={totalClinics}
            subtitle="across all companies"
            icon={<BusinessIcon />}
            color="#8b5cf6"
          />
        </Grid>
      </Grid>

      {/* Search and Filters */}
      <Card sx={{ border: '1px solid #e2e8f0', mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <TextField
              placeholder="Search companies..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              size="small"
              sx={{ minWidth: 250 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                )
              }}
            />
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                label="Status"
              >
                <MenuItem value="all">All Status</MenuItem>
                <MenuItem value="Active">Active</MenuItem>
                <MenuItem value="At Risk">At Risk</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Tier</InputLabel>
              <Select
                value={tierFilter}
                onChange={(e) => setTierFilter(e.target.value)}
                label="Tier"
              >
                <MenuItem value="all">All Tiers</MenuItem>
                <MenuItem value="Enterprise">Enterprise</MenuItem>
                <MenuItem value="Premium">Premium</MenuItem>
                <MenuItem value="Standard">Standard</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </CardContent>
      </Card>

      {/* Companies Table */}
      <Card sx={{ border: '1px solid #e2e8f0' }}>
        <CardContent sx={{ p: 0 }}>
          <Box sx={{ p: 3, bgcolor: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
            <Typography variant="h6" sx={{ fontWeight: 600, color: '#0f172a' }}>
              Companies ({filteredCompanies.length})
            </Typography>
            <Typography variant="body2" sx={{ color: '#64748b' }}>
              Manage your client portfolio and relationships
            </Typography>
          </Box>
          
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: '#f8fafc' }}>
                  <TableCell sx={{ fontWeight: 600, color: '#374151' }}>Company</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#374151' }}>Location</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#374151' }}>Industry</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#374151' }}>Tier</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#374151' }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#374151' }}>Rep</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#374151' }}>Clinics</TableCell>
                  {showGMV && (
                    <TableCell sx={{ fontWeight: 600, color: '#374151' }}>Revenue</TableCell>
                  )}
                  <TableCell sx={{ fontWeight: 600, color: '#374151' }}>Last Order</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#374151' }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredCompanies.map((company) => (
                  <TableRow key={company.id} hover>
                    <TableCell>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#0f172a' }}>
                          {company.name}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#64748b' }}>
                          {company.users} users
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {company.city && company.state ? `${company.city}, ${company.state}` : 'Not specified'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {company.industry || 'Not specified'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {company.tier && <StatusBadge status={company.tier} />}
                    </TableCell>
                    <TableCell>
                      <StatusBadge status={company.status} />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ color: '#374151' }}>
                        {company.repName || 'Unassigned'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {company.clinics}
                      </Typography>
                    </TableCell>
                    {showGMV && (
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {formatCurrency(company.revenue12m)}
                        </Typography>
                      </TableCell>
                    )}
                    <TableCell>
                      <Typography variant="body2">
                        {company.lastOrder ? new Date(company.lastOrder).toLocaleDateString() : 'No recent orders'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <IconButton
                        size="small"
                        onClick={() => handleViewCompany(company.id)}
                        sx={{ color: '#0d9488' }}
                      >
                        <ViewIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    </Box>
  );
};

export default CompanyManagement;
